import styled from "styled-components";
import { useState, useEffect } from "react";
import PrimaryButton from "../../../../../components/FormControls/PrimaryButton";
import SecondaryButton from "../../../../../components/FormControls/SecondaryButton";
import NarrowGrid from "../../../../../components/NarrowGrid";
import FormSelectWithLabel from "../../../../../components/FormControls/FormSelectWithLabel";
import FormDatePicker from "../../../../../components/FormControls/FormDatePicker";
import request from "../../../../../api/request";
import { NotificationManager } from 'react-notifications';

const StyledFinancialYears = styled.div`
    margin-left: 30px;
`;

const WhiteBoard = styled.div`
    background-color: white;
    width: 750px;
    box-sizing: border-box;
    min-height: 460px;
    padding: 30px;
    margin-bottom: 30px;
    color: ${(props) => props.theme.color.gray};
`;

function FinancialYears(props) {

    let currentYear = new Date().getFullYear();

    let years = [];
    for (let i = currentYear - 10; i < currentYear + 5; i ++) {
        years.push(i);
    }

    const [loadedPage, setLoadedPage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
    const [selectedFinancialYearId, setSelectedFinancialYearId] = useState(null);
    const [isAddMode, setIsAddMode] = useState(false);
    const [gridColumns, setGridColumns] = useState([
        {id: 'Year', type: 'String', width: 60, textAlign: 'left'},
        {id: 'From', type: 'String', width: 80, textAlign: 'center'},
        {id: 'To', type: 'String', width: 80, textAlign: 'center'},
    ]);
    const [gridData, setGridData] = useState({groups: [''], items: [[]]});
    const [accountingYear, setAccountingYear] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [needsValidation, setNeedsValidation] = useState(false);
    const [isAddingDuplicatedItem, setIsAddingDuplicatedItem] = useState(false);

    const convertDateToUTCTimeZone = (val) => {
        let diffMinutes = val.getTimezoneOffset();
        val.setTime(val.getTime() - diffMinutes*60*1000);
        return val;
    }

    const dateToStr = (date) => {
        function pad(s) { return (s < 10) ? '0' + s : s; };
        let ret = [pad(date.getDate()), pad(date.getMonth()+1), date.getFullYear()].join('/');
        return ret;
    }

    const strToDate = (str) => {
        var date_components = str.split("/");
        var day = date_components[0];
        var month = date_components[1];
        var year = date_components[2];
        return new Date(year, month - 1, day);
    }

    const onSelectGridItem = (item) => {
        setSelectedFinancialYearId(item.Id);
        setSelectedFinancialYear(item);
    }
    
    const fetchFinancialYears = async (accountingYearToSelect) => {
        try {
            let itemList = [];

            const resp = (await request('database', 'fetchFinancialYears'));
            if (resp.ok) {
                resp.data.map((item, index) => {
                    itemList.push(item);
                });
                setFinancialYears(itemList);
                rebuildGridData(itemList, accountingYearToSelect);
            } else {
                console.log(resp.data);
                NotificationManager.error(resp.data);
                return false;
            }
        } catch (e) {
            console.log('Failed to fetch financial years: ', e);
            return false;
        }
        return true;
    }

    const onDeleteFinancialYear = async () => {
        if (selectedFinancialYear) {
            setIsDeleting(true);
            let deleteData = {};
            deleteData.AccountingYear = selectedFinancialYear.AccountingYear;

            const resp = (await request('database', 'deleteFinancialYear', [deleteData]));
            if (resp.ok) {
                setSelectedFinancialYear(null);
                setSelectedFinancialYearId(null);
                fetchFinancialYears();
                NotificationManager.success('Successfully deleted financial year');
            } else
            {
                console.log(resp.data);
                NotificationManager.error(resp.data);
            }
            setIsDeleting(false);
        }
    }

    const onSaveChanges = async () => {
        if (isAddMode) { // adding new item
            if (startDate === null || endDate === null) {
                setNeedsValidation(true);
            } else {
                // check duplicated code
                if (gridData.items[0].length > 0 && (gridData.items[0].find((itm) => itm.AccountingYear === accountingYear))) {
                    setIsAddingDuplicatedItem(true);
                } else {
                    setIsSaving(true);
                    let saveData = {};
                    saveData.AccountingYear = accountingYear;
                    saveData.StartDate = convertDateToUTCTimeZone(startDate);
                    saveData.EndDate = convertDateToUTCTimeZone(endDate);

                    const resp = (await request('database', 'createFinancialYear', [saveData]));
                    if (resp.ok) {
                        fetchFinancialYears(accountingYear);
                        NotificationManager.success('Successfully added financial year');
                    } else
                    {
                        console.log(resp.data);
                        NotificationManager.error(resp.data);
                    }
                    setIsSaving(false);
                    setIsAddMode(false);
                }
            }
        } else {
            if (startDate === null || endDate === null) {
                setNeedsValidation(true);
            } else {
                setIsSaving(true);
                let saveData = {};
                saveData.AccountingYear = accountingYear;
                saveData.StartDate = convertDateToUTCTimeZone(startDate);
                saveData.EndDate = convertDateToUTCTimeZone(endDate);

                const resp = (await request('database', 'updateFinancialYear', [saveData]));
                if (resp.ok) {
                    fetchFinancialYears(accountingYear);
                    NotificationManager.success('Successfully updated financial year');
                } else
                {
                    console.log(resp.data);
                    NotificationManager.error(resp.data);
                }
                setIsSaving(false);
            }
        }
    }

    const rebuildGridData = (items, accountingYearToSelect) => {
        if (items && items.length > 0) {
            let itemList = [];
            items.map((item, index) => {
                let gridItem = {Id: index, Year: item.AccountingYear.toString(), From: dateToStr(new Date(item.StartDate)), To: dateToStr(new Date(item.EndDate)), ...item};
                itemList.push(gridItem);
                if (accountingYearToSelect && item.AccountingYear === accountingYearToSelect) {
                    setSelectedFinancialYearId(index);
                    setSelectedFinancialYear(gridItem);
                }
            });
            setGridData({groups: [''], items: [itemList]});
        }
    }

    useEffect(() => {
        if (!loadedPage) {
            props.setPageTitle('Financial years');
            fetchFinancialYears();
            setLoadedPage(true);
        }
    }, []);

    useEffect(() => {
        if (isAddMode) {
            setNeedsValidation(false);
            setAccountingYear(currentYear);
            setStartDate(null);
            setEndDate(null);
        } else if (selectedFinancialYear) {
            setNeedsValidation(false);
            setAccountingYear(selectedFinancialYear.AccountingYear);
            setStartDate(strToDate(selectedFinancialYear.From));
            setEndDate(strToDate(selectedFinancialYear.To));
        }
    }, [isAddMode]);

    useEffect(() => {
        if (selectedFinancialYear) {
            setNeedsValidation(false);
            setAccountingYear(selectedFinancialYear.AccountingYear);
            setStartDate(strToDate(selectedFinancialYear.From));
            setEndDate(strToDate(selectedFinancialYear.To));
        } else {
            setNeedsValidation(false);
            setAccountingYear(currentYear);
            setStartDate(null);
            setEndDate(null);
        }
    }, [selectedFinancialYear]);

    useEffect(() => {
        setIsAddingDuplicatedItem(false);
        setNeedsValidation(false);
    }, [selectedFinancialYear, isAddMode]);

    return (
        <StyledFinancialYears>
            <WhiteBoard>
                <div style={{display:'flex'}}>
                    <div>
                        <NarrowGrid id='generaltables_financialyears_grid' gridColumns={gridColumns} gridData={gridData} disabled={isAddMode} itemIdToSelect={selectedFinancialYearId} onSelectGridItem={onSelectGridItem} gridStyle={{width:220, height:350, marginTop:20}}></NarrowGrid>
                        <div style={{marginTop:20}}>
                            {selectedFinancialYearId !== null && !isAddMode && <SecondaryButton caption='Delete' isLoading={isDeleting} onClick={() => onDeleteFinancialYear()} style={{width:75, height:25}}></SecondaryButton>}
                            {isAddMode && <SecondaryButton caption='Cancel' onClick={() => setIsAddMode(false)} style={{width:75, height:25, float:'right'}}></SecondaryButton>}
                            {!isAddMode && <PrimaryButton caption='Create new' onClick={() => setIsAddMode(true)} style={{width:100, height:25, float:'right'}}></PrimaryButton>}
                        </div>
                    </div>
                    <div style={{marginLeft:30}}>
                    <FormSelectWithLabel label='Year' items={years} value={accountingYear} disabled={!isAddMode} onChange={(e) => setAccountingYear(parseInt(e.target.value))} style={{width:100, height:35, paddingLeft:13}}></FormSelectWithLabel>
                    {isAddingDuplicatedItem && <div style={{marginTop:5, width:280, fontSize:12, fontWeight:700, color:'#dc3545'}}>* Accounting year is duplicated.</div>}
                    <FormDatePicker label='From' value={startDate} onChange={(val) => setStartDate(val)} style={{width:280}} validated={needsValidation && startDate === null ? false : undefined} disabled={selectedFinancialYearId === null && !isAddMode} ></FormDatePicker>
                    <FormDatePicker label='To' value={endDate} onChange={(val) => setEndDate(val)} style={{width:280}} validated={needsValidation && endDate === null ? false : undefined} disabled={selectedFinancialYearId === null && !isAddMode} ></FormDatePicker>
                    </div>
                </div>
            </WhiteBoard>
            <div>
                <PrimaryButton caption={isAddMode ? 'Add financial year' : 'Save changes'} isLoading={isSaving} onClick={() => onSaveChanges()} disabled={isAddMode || selectedFinancialYear ? false : true} style={{width:150, float:'right'}}></PrimaryButton>
            </div>
        </StyledFinancialYears>
    )
}

export default FinancialYears;